import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as modelHelper from "../helpers/modelHelper";
import * as reducerHelper from "../helpers/reducerHelper";

const initialState = {
    measurementResult: null,
    fandeck: null,
    color: null,
    cif: null,
    targetMeasurementResult: null,
    targetFormula: null,
    targetCif: null,
    matchingResultList: null,
    matchingResultCode: null,
    matchingResultMessage: null,
    activeMatchingResult: null,
    activeProduct: null,
    activeColorfile: null,
    activeBases: [],
    activePackage: null,
    activeColorants: [],
    activeTemplate: null,
    fandeckList: [],
    colorList: [],
    colorfileList: [],
    baseList: [],
    packageList: [],
    colorantList: [],
    templateList: [],
    tabScene: null,
    selectedProduct: null
};

/**
 * Structure of matching tab store:
 * - measurementResult      {Object | null}
 * - fandeck                {Object | null}
 * - color                  {Object | null}
 * - cif                    {Object | null}
 * - targetMeasurementResult{Object | null}
 * - targetFormula          {Object | null}
 * - targetCif              {Object | null}
 * - matchingResultList     {Array | null}
 * - matchingResultCode     {Number | null}
 * - matchingResultMessage  {String | null}
 * - activeMatchingResult   {Object | null}
 * - activeProduct          {Object | null}
 * - activeColorfile        {Object | null}
 * - activeBases            {Array}
 * - activePackage          {Object | null}
 * - activeColorants        {Array}
 * - activeTemplate         {Object | null}
 * - fandeckList            {Array}
 * - colorList              {Array}
 * - colorfileList          {Array}
 * - baseList               {Array}
 * - packageList            {Array}
 * - colorantList           {Array}
 * - templateList           {Array}
 * - tabScene               {String | null}
 * - selectedProduct        {Object | null}
 */
export const matchingTabReducer = (state = {}, action) => {
    switch (action.type) {
        case actionTypes.MATCHING_TAB_INIT:
            if (state[action.payload.tabId] !== undefined) {
                return state;
            }

            let initState = {};
            initState[action.payload.tabId] = action.payload.product
                ? {...initialState, selectedProduct: action.payload.product}
                : initialState;
            return {...state, ...initState};
        case actionTypes.CORRECTION_SAVE_SPECTRAL_DATA:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    measurementResult: action.payload.result,
                    fandeck: null,
                    color: null,
                    cif: action.payload.cif ?? null,
                    matchingResultList: null,
                    matchingResultCode: null,
                    matchingResultMessage: null,
                    fandeckList: [],
                    colorList: []
                },
                state
            );
        case actionTypes.CORRECTION_SAVE_TARGET_FORMULA:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    targetMeasurementResult: action.payload.result,
                    targetFormula: action.payload.formula,
                    targetCif: modelHelper.mapCifEntitiesToObject(0, action.payload.color, action.payload.fandeck),
                    activeProduct: action.payload.product,
                    activeBases: [action.payload.base],
                    activePackage: action.payload.package,
                    tabScene: componentTypes.AUTO_CORRECTION_SCENE
                },
                state
            );
        case actionTypes.MATCHING_SAVE_MEASURE_RESULT_AND_OPEN_PREVIEW:
        case actionTypes.CORRECTION_SAVE_MEASUREMENT_RESULT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    measurementResult: action.payload.result,
                    fandeck: null,
                    color: null,
                    cif: null,
                    matchingResultList: null,
                    matchingResultCode: null,
                    matchingResultMessage: null,
                    fandeckList: [],
                    colorList: []
                },
                state
            );
        case actionTypes.CORRECTION_SAVE_TARGET_COLOR_MEASUREMENT_RESULT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    targetMeasurementResult: action.payload.result,
                    targetCif: null,
                    matchingResultList: null,
                    matchingResultCode: null,
                    matchingResultMessage: null
                },
                state
            );
        case actionTypes.CORRECTION_SAVE_TARGET_COLOR_SPECTRAL_DATA:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeck: null,
                    color: null,
                    targetMeasurementResult: action.payload.result,
                    targetCif: action.payload.cif,
                    matchingResultList: null,
                    matchingResultCode: null,
                    matchingResultMessage: null,
                    fandeckList: [],
                    colorList: []
                },
                state
            );
        case actionTypes.MATCHING_TAB_SELECT_FANDECK:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeck: action.payload.fandeck
                },
                state
            );
        case actionTypes.MATCHING_TAB_SELECT_COLOR:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    color: action.payload.color
                },
                state
            );
        case actionTypes.MATCHING_TAB_SELECT_PRODUCT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeProduct: action.payload.product,
                    activeColorfile: null,
                    activeBases: [],
                    activePackage: null,
                    activeColorants: [],
                    activeTemplate: null,
                    colorfileList: [],
                    baseList: [],
                    packageList: [],
                    colorantList: [],
                    templateList: []
                },
                state
            );
        case actionTypes.MATCHING_TAB_SELECT_COLORFILE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeColorfile: action.payload.colorfile,
                    activeBases: [],
                    activePackage: null,
                    activeColorants: [],
                    activeTemplate: null,
                    baseList: [],
                    packageList: [],
                    colorantList: [],
                    templateList: []
                },
                state
            );
        case actionTypes.MATCHING_TAB_SELECT_CORRECTION_COLORFILE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeColorfile: action.payload.colorfile
                },
                state
            );
        case actionTypes.MATCHING_TAB_SELECT_BASE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            let baseList = [...state[action.payload.tabId].activeBases, action.payload.base];

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeBases: baseList,
                    activePackage: null,
                    activeColorants: [],
                    packageList: [],
                    colorantList: reducerHelper.mergeLists(baseList.map((base) => base.colorantList))
                },
                state
            );
        case actionTypes.MATCHING_TAB_REMOVE_BASE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            let newBaseList = state[action.payload.tabId].activeBases.filter(
                (item) => item.id !== action.payload.base.id
            );

            if (newBaseList.length === 0) {
                return reducerHelper.getNewState(
                    action.payload.tabId,
                    {
                        activeBases: [],
                        activePackage: null,
                        activeColorants: [],
                        packageList: [],
                        colorantList: []
                    },
                    state
                );
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeBases: newBaseList,
                    packageList: [],
                    colorantList: reducerHelper.mergeLists(newBaseList.map((base) => base.colorantList))
                },
                state
            );
        case actionTypes.MATCHING_TAB_SELECT_PACKAGE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activePackage: action.payload.selectedPackage
                },
                state
            );

        case actionTypes.MATCHING_TAB_SET_COLORANT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeColorants: action.payload.colorants
                },
                state
            );

        case actionTypes.MATCHING_TAB_SELECT_TEMPLATE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeTemplate: action.payload.template
                },
                state
            );
        case actionTypes.MATCHING_TAB_SAVE_FANDECK_LIST:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeckList: action.payload.fandeckList
                },
                state
            );
        case actionTypes.MATCHING_TAB_SAVE_FANDECK_LIST_WITH_OFFSET:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    fandeckList: state[action.payload.tabId].fandeckList.concat(action.payload.fandeckList)
                },
                state
            );
        case actionTypes.MATCHING_TAB_SAVE_COLOR_LIST:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    colorList: action.payload.colorList
                },
                state
            );
        case actionTypes.MATCHING_TAB_SAVE_COLOR_LIST_WITH_OFFSET:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    colorList: state[action.payload.tabId].colorList.concat(action.payload.colorList)
                },
                state
            );
        case actionTypes.MATCHING_TAB_SAVE_COLORFILE_LIST:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeColorfile: null,
                    colorfileList: action.payload.colorfileList
                },
                state
            );
        case actionTypes.MATCHING_SAVE_BASE_LIST:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeBases: [...action.payload.baseList],
                    activePackage: null,
                    baseList: action.payload.baseList,
                    packageList: [],
                    colorantList: reducerHelper.mergeLists(action.payload.baseList.map((base) => base.colorantList))
                },
                state
            );
        case actionTypes.MATCHING_SAVE_PACKAGE_LIST:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            const packageList = reducerHelper.getUniquePackageList(action.payload.packageList);
            const sortedPackageList = packageList.sort((a, b) => {
                return a.filling - b.filling;
            });

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activePackage: sortedPackageList.length > 0 ? sortedPackageList[0] : null,
                    packageList: sortedPackageList
                },
                state
            );
        case actionTypes.MATCHING_SAVE_COLORANT_LIST:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    colorantList: action.payload.colorantList
                },
                state
            );
        case actionTypes.MATCHING_SAVE_TEMPLATE_LIST:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeTemplate: action.payload.templateList.length > 0 ? action.payload.templateList[0] : null,
                    templateList: action.payload.templateList
                },
                state
            );
        case actionTypes.MATCHING_SAVE_RESULT:
        case actionTypes.CORRECTION_SAVE_RESULT:
        case actionTypes.REFORMULATION_SAVE_RESULT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    matchingResultList: action.payload.resultList,
                    matchingResultCode: action.payload.resultCode,
                    matchingResultMessage: action.payload.resultMessage,
                    activeMatchingResult: action.payload.resultList.length > 0 ? action.payload.resultList[0] : null
                },
                state
            );
        case actionTypes.MATCHING_TAB_SELECT_MATCHING_RESULT:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    activeMatchingResult: action.payload.matchingResult
                },
                state
            );
        case actionTypes.MATCHING_TAB_SET_DRAFT_FORMULA_SCENE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    tabScene: componentTypes.DRAFT_FORMULA_SCENE
                },
                state
            );
        case actionTypes.MATCHING_TAB_SET_CORRECTION_SCENE:
            if (state[action.payload.tabId] === undefined) {
                return state;
            }

            return reducerHelper.getNewState(
                action.payload.tabId,
                {
                    measurementResult: action.payload.measurementResult,
                    fandeck: null,
                    color: null,
                    cif: null,
                    targetMeasurementResult: action.payload.targetMeasurementResult,
                    targetFormula: action.payload.targetFormula,
                    targetCif: null,
                    matchingResultList: null,
                    matchingResultCode: null,
                    matchingResultMessage: null,
                    activeMatchingResult: null,
                    fandeckList: [],
                    colorList: [],
                    tabScene: componentTypes.AUTO_CORRECTION_SCENE
                },
                state
            );
        case actionTypes.CLOSE_ACTIVE_TAB:
        case actionTypes.CLOSE_ALL_TABS_EXCEPT_ACTIVE:
        case actionTypes.CLOSE_ALL_TABS:
            let closeState = {};

            let tab;
            for (tab of action.payload.newTabList) {
                if (state[tab.id] !== undefined) {
                    closeState[tab.id] = state[tab.id];
                }
            }

            return closeState;
        case actionTypes.LOG_OUT_SUCCESS:
        case actionTypes.WEBSOCKET_CLOSED:
        case actionTypes.DATABASE_CHANGE:
            return {};
        default:
            return state;
    }
};
